import {
	Box,
	FormControlLabel,
	Grid,
	Radio,
	RadioGroup,
	TextField,
} from "@mui/material";
import { addDays, isAfter } from "date-fns";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { DateRangePicker } from "rsuite";

import ControlledDatePicker from "../controlled/ControlledDatePicker";
import ControlledLocationAutocomplete from "../controlled/ControlledLocationAutocomplete";
import SetValueButton from "../SetValueButton";

import { useTranslation } from "react-i18next";
import { StyledDateRangePicker } from "../StyledDateRangePicker";
import { StyledTimePicker } from "../StyledTimePicker";

const { combine, before, after } = DateRangePicker;

export const UnloadingPartRanges = ({
	control,
	errors,
	setValue,
	watch,
	resetAutocompleteKey,
	addressDefaultValue,
}) => {
	const { t } = useTranslation();
	const [dateType, setDateType] = useState("day");

	const watchLoadingDateType = watch("loadingDateType");
	const watchUnloadingDateType = watch("unloadingDateType");
	const watchLoadingDate = watch("loadingDate");
	const watchLoadingDateRange = watch("loadingDateRange");
	// const watchLoadingRepeat = watch("loadingRepeat");

	const [minDate, setMinDate] = useState(new Date());
	const [maxDate, setMaxDate] = useState(addDays(new Date(), 30));
	useEffect(() => {
		if (watchLoadingDateType === "day" && watchLoadingDate) {
			watchLoadingDate?._d
				? setMinDate(new Date(watchLoadingDate._d))
				: setMinDate(new Date(watchLoadingDate));

			watchLoadingDate?._d
				? setMaxDate(addDays(new Date(watchLoadingDate._d), 30))
				: setMaxDate(addDays(new Date(watchLoadingDate), 30));
		}
		if (
			watchLoadingDateType === "range" &&
			watchUnloadingDateType === "day" &&
			watchLoadingDateRange?.length > 0
		) {
			setMinDate(new Date(watchLoadingDateRange[1]));

			setMaxDate(addDays(new Date(watchLoadingDateRange[0]), 30));
		}
		if (
			watchLoadingDateType === "range" &&
			watchUnloadingDateType === "range" &&
			watchLoadingDateRange?.length > 0
		) {
			setMinDate(new Date(watchLoadingDateRange[0]));

			setMaxDate(addDays(new Date(watchLoadingDateRange[0]), 30));
		}
	}, [
		watchLoadingDateType,
		watchLoadingDate,
		watchLoadingDateRange,
		watchUnloadingDateType,
	]);

	useEffect(() => {
		setDateType(watchUnloadingDateType);
	}, [watchLoadingDateType, watchUnloadingDateType]);

	return (
		<Grid
			container
			rowSpacing={0}
			columnSpacing={1}
			sx={{ py: 1, px: 2, position: "relative" }}
		>
			<Box
				sx={{
					position: "absolute",
					top: "-12px",
					left: "105px",
					background: "#fff",
					border: "1px solid #7f7f7f",
					height: "24px",
					width: "156px",
					display: "flex",
					alignItems: "center",
				}}
			>
				<Controller
					control={control}
					name="unloadingDateType"
					defaultValue="day"
					render={({ field: { onChange, value } }) => (
						<RadioGroup
							row
							name="unloadingDateType"
							value={value}
							onChange={(e) => {
								onChange(e);
								setDateType((prev) => (prev === "day" ? "range" : "day"));
							}}
						>
							<FormControlLabel
								sx={{ ml: "11px", mr: "22px" }}
								value="day"
								control={
									<Radio
										sx={{
											padding: 0,
											"& .MuiSvgIcon-root": {
												fontSize: 16,
											},
										}}
									/>
								}
								label={t("day")}
							/>
							<FormControlLabel
								sx={{ mr: 0 }}
								value="range"
								control={
									<Radio
										sx={{
											padding: 0,
											mr: 0,
											"& .MuiSvgIcon-root": {
												fontSize: 16,
											},
										}}
									/>
								}
								label={t("range_of_days")}
							/>
						</RadioGroup>
					)}
				/>
			</Box>
			<Grid
				size={{
					lg: 8,
				}}
			>
				<ControlledLocationAutocomplete
					resetKey={resetAutocompleteKey}
					label={t("postal_code_city_country_label")}
					control={control}
					margin="normal"
					name="unloadingAddress"
					defaultValue={addressDefaultValue}
					setFormValue={setValue}
					errors={errors}
				/>
			</Grid>
			{dateType === "day" && (
				<Grid
					size={{
						lg: 4,
					}}
				>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							position: "relative",
						}}
					>
						<ControlledDatePicker
							control={control}
							margin="normal"
							name="unloadingDate"
							label={t("date")}
							error={!!errors?.unloadingDate}
							minDate={minDate}
							maxDate={maxDate}
						/>
						<Box
							sx={{
								position: "absolute",
								top: "4px",
								left: "72px",
								display: "flex",
								gap: "6px",
							}}
						>
							<SetValueButton
								title={t("today")}
								style={{ padding: "2px 5px" }}
								type="button"
								disabled={isAfter(minDate, new Date())}
								onClick={() =>
									setValue("unloadingDate", new Date(), {
										shouldValidate: true,
									})
								}
							>
								{t("today_short")}
							</SetValueButton>
							<SetValueButton
								title={t("tomorrow")}
								style={{ padding: "2px 5px" }}
								type="button"
								disabled={isAfter(minDate, addDays(new Date(), 1))}
								onClick={() =>
									setValue("unloadingDate", addDays(new Date(), 1), {
										shouldValidate: true,
									})
								}
							>
								{t("tomorrow_short")}
							</SetValueButton>
							<SetValueButton
								title={t("after_tomorrow")}
								style={{ padding: "2px 5px" }}
								type="button"
								disabled={isAfter(minDate, addDays(new Date(), 2))}
								onClick={() =>
									setValue("unloadingDate", addDays(new Date(), 2), {
										shouldValidate: true,
									})
								}
							>
								{t("after_tomorrow_short")}
							</SetValueButton>
						</Box>
					</Box>
				</Grid>
			)}
			{dateType === "range" && (
				<Grid
					size={{
						lg: 4,
					}}
				>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							position: "relative",
							gap: "4px",
							height: "100%",
							top: "2px",
						}}
					>
						<Controller
							control={control}
							name="unloadingTimeStart"
							render={({ field: { onChange, value } }) => {
								return (
									<StyledTimePicker
										label={t("hours_label") + " *"}
										name="unloadingTimeStart"
										value={value || null}
										onChange={onChange}
										error={!!errors?.unloadingTimeStart}
										textFieldSx={{}}
									/>
								);
							}}
						/>
						<span>-</span>
						<Controller
							control={control}
							name="unloadingTimeEnd"
							// defaultValue="day"
							render={({ field: { onChange, value } }) => {
								return (
									<StyledTimePicker
										label={t("hours_label")}
										name="unloadingTimeEnd"
										value={value || null}
										onChange={onChange}
										error={!!errors?.unloadingTimeEnd}
										textFieldSx={{}}
									/>
								);
							}}
						/>
					</Box>
				</Grid>
			)}
			<Grid
				size={{
					lg: dateType === "range" ? 4 : 8,
				}}
			>
				<Box
					sx={{
						alignItems: "center",
						display: "flex",
						height: "100%",
						gap: "10px",
					}}
				>
					{/* Hidden field for grid alignment */}
					<TextField
						size="small"
						value={0}
						variant="outlined"
						sx={{
							maxWidth: "164px",
							mt: "16px",
							mb: "8px",
							visibility: "hidden",
						}}
						disabled
						slotProps={{
							inputLabel: { shrink: true },
						}}
					/>
				</Box>
			</Grid>
			{dateType === "day" && (
				<Grid
					size={{
						lg: 4,
					}}
				>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							position: "relative",
							gap: "4px",
							height: "100%",
							top: "4px",
						}}
					>
						<Controller
							control={control}
							name="unloadingTimeStart"
							render={({ field: { onChange, value } }) => (
								<StyledTimePicker
									label={t("hours_label") + " *"}
									name="unloadingTimeStart"
									value={value || null}
									onChange={onChange}
									error={!!errors?.unloadingTimeStart}
									textFieldSx={{}}
								/>
							)}
						/>
						<span>-</span>
						<Controller
							control={control}
							name="unloadingTimeEnd"
							// defaultValue="day"
							render={({ field: { onChange, value } }) => (
								<StyledTimePicker
									label={t("hours_label")}
									name="unloadingTimeEnd"
									value={value || null}
									onChange={onChange}
									error={!!errors?.unloadingTimeEnd}
									textFieldSx={{}}
								/>
							)}
						/>
					</Box>
				</Grid>
			)}
			{dateType === "range" && (
				<Grid
					size={{
						lg: 8,
					}}
				>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							position: "relative",
							mt: "16px",
							mb: "8px",
						}}
					>
						<Controller
							control={control}
							name="unloadingDateRange"
							// defaultValue="day"
							render={({ field: { onChange, value } }) => (
								<StyledDateRangePicker
									name="unloadingDateRange"
									value={value}
									onChange={onChange}
									error={!!errors?.unloadingDateRange}
									shouldDisableDate={combine(before(minDate), after(maxDate))}
								/>
							)}
						/>
					</Box>
				</Grid>
			)}
		</Grid>
	);
};
